import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatPt } from '../../../lib/format.js';
import { BAR_MAX_SIZE } from './chart-styles.js';
import { DEVELOP_COLOR, REQUIREMENT_COLOR } from './develop-point-view.js';
import type { KpiTrendWip } from '../../../types/api.js';

interface RequirementBreakdownChartsProps {
  /** 当月の要件定義セット実績pt (MTD)。欠測は null */
  requirementActual: number | null;
  /** 当月の開発pt 実績から要件定義分を除いた値 (MTD)。欠測は null */
  developActual: number | null;
  /** 当月の要件定義予算の**現時点進捗目標**（JP 営業日で日割りした MTD 累積）。v2 シートが無い月は null */
  requirementTarget: number | null;
  /** 当月の開発pt 予算から要件定義分を除いた**現時点進捗目標**（日割りした MTD 累積）。v2 シートが無い月は null */
  developTarget: number | null;
  /**
   * 現時点の処理中pt（要件定義 / それ以外）。集計できなかった場合は undefined で、
   * そのときは処理中の表示（破線枠）自体を出さない（0pt と欠測を区別するため 0 に倒さない）。
   */
  wip?: KpiTrendWip;
}

/** 円グラフ / 棒グラフ共通のツールチップ見た目 @graph-connects none */
const TOOLTIP_STYLE = {
  backgroundColor: 'var(--color-bg-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  boxShadow: 'var(--shadow-lg)',
};

/**
 * 処理中（破線枠）の塗り不透明度。枠線を主役にして「まだ完了していない量」を表すため、
 * 塗りは系統色が分かる程度に薄く乗せる。
 * @graph-connects none
 */
const WIP_FILL_OPACITY = 0.18;

/** 処理中（破線枠）の破線パターン。円・棒・凡例で共通 @graph-connects none */
const WIP_DASH_ARRAY = '3 2';

/** 予算バーの塗り不透明度（実績バーより淡くして同色内で区別する） @graph-connects none */
const BUDGET_BAR_OPACITY = 0.35;

/**
 * 系統色（6 桁 hex）に 15% 相当のアルファを付けた塗り色を返す。
 * 凡例の破線スウォッチを「薄い塗り + 実線色の破線枠」にするために使う。
 * `REQUIREMENT_COLOR` / `DEVELOP_COLOR` はどちらも 6 桁 hex 固定なので 8 桁化してよい。
 * @graph-connects none
 */
function tint(color: string): string {
  return `${color}26`;
}

/** 円グラフ 1 スライス分のデータ @graph-connects none */
interface PieSlice {
  name: string;
  value: number;
  color: string;
  /** true = 処理中（破線枠・薄塗り）、false = 完了（実塗り） */
  inProgress: boolean;
  /** 処理中スライスのチケット件数（完了スライスは undefined） */
  tickets?: number;
}

/**
 * 円グラフのスライスを payload の色・状態で塗る shape レンダラー。
 * 完了は実塗り、処理中は薄塗り + 系統色の破線枠にして、**色は工程・塗りは状態**という
 * 2 軸の読み方を棒グラフと揃える。
 * recharts の `Cell` が deprecated なため、`DonutBlock` / `DevelopPointsTrendCard` と同じ
 * shape レンダラー方式でデータごとの見た目を当てる。
 * @graph-connects none
 */
function sliceShape(props: unknown) {
  const p = props as {
    cx: number;
    cy: number;
    innerRadius: number;
    outerRadius: number;
    startAngle: number;
    endAngle: number;
    payload: PieSlice;
  };
  const { inProgress, color } = p.payload;
  return (
    <Sector
      cx={p.cx}
      cy={p.cy}
      innerRadius={p.innerRadius}
      outerRadius={p.outerRadius}
      startAngle={p.startAngle}
      endAngle={p.endAngle}
      fill={color}
      fillOpacity={inProgress ? WIP_FILL_OPACITY : 1}
      stroke={inProgress ? color : 'var(--color-bg-surface)'}
      strokeWidth={inProgress ? 1.5 : 1}
      strokeDasharray={inProgress ? WIP_DASH_ARRAY : undefined}
    />
  );
}

/** 全体に占める比率（%, 小数 1 桁）。母数 0 以下は 0 @graph-connects none */
function slicePercent(value: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((value / total) * 1000) / 10;
}

/** 棒グラフ 1 カテゴリ分のデータ @graph-connects none */
interface BarGroup {
  /** x 軸に出す短縮名（フルネームは凡例が持つ。長いと recharts がティックを間引いて片方消える） */
  category: string;
  color: string;
  /** 現時点進捗目標（日割りした MTD 累積）の予算。欠測は null */
  target: number | null;
  /** 当月累計の実績（完了）。欠測は null */
  actual: number | null;
  /** 現時点の処理中pt。集計できなかった場合は null（破線枠を描かない） */
  wip: number | null;
  /** 処理中pt のチケット件数。処理中が欠測なら null */
  wipTickets: number | null;
  /** 完了 + 処理中。処理中が欠測なら null */
  actualTotal: number | null;
  /** カテゴリ内の最大値で正規化した予算（0..1）。棒の高さに使う */
  targetRatio: number | null;
  /** カテゴリ内の最大値で正規化した実績（完了）。積み棒の下段 */
  actualRatio: number | null;
  /** カテゴリ内の最大値で正規化した処理中pt。積み棒の上段（破線枠） */
  wipRatio: number | null;
}

/**
 * 棒の高さの上限（正規化後の y 軸ドメイン上端）。
 * カテゴリ内最大の棒は 1.0 になるので、1.0 のままだと頂点の数値ラベルが描画領域から
 * はみ出て切れる。頭上に余白を作るため少し広く取る。
 * @graph-connects none
 */
const BAR_RATIO_DOMAIN_MAX = 1.18;

/**
 * カテゴリ内の最大値を 1.0 とする正規化値を足す。
 *
 * 要件定義（数百pt）と開発pt（全体スコープでは数千pt）を同一尺度に乗せると要件定義側の棒が
 * 潰れて 予算 / 実績 の差が読めない。比べたいのは「その枠をどれだけ使ったか」であって
 * 要件定義と開発ptの絶対量ではないので、**カテゴリごとに尺度を切り**、y 軸は出さず
 * 絶対値は各棒の数値ラベルで読ませる。
 *
 * 尺度の母数は **予算 と「完了 + 処理中」の大きい方**。完了だけで取ると処理中を積んだ
 * 積み棒が `BAR_RATIO_DOMAIN_MAX` の頭上余白を突き抜け、頂点の数値ラベルが切れる。
 * @graph-connects none
 */
function withCategoryScale(
  group: Omit<BarGroup, 'targetRatio' | 'actualRatio' | 'wipRatio' | 'actualTotal'>,
): BarGroup {
  const actualTotal = group.wip === null ? null : (group.actual ?? 0) + group.wip;
  const scale = Math.max(group.target ?? 0, actualTotal ?? group.actual ?? 0);
  const ratio = (value: number | null) => {
    if (value === null) return null;
    return scale > 0 ? value / scale : 0;
  };
  return {
    ...group,
    actualTotal,
    targetRatio: ratio(group.target),
    actualRatio: ratio(group.actual),
    wipRatio: ratio(group.wip),
  };
}

/**
 * 棒 1 本を payload のカテゴリ色で塗る shape レンダラーを作る。
 *
 * 1 系列（予算 / 実績 / 処理中）が 要件定義=水色・開発pt=黄 の 2 色をまたぐため、系列固定の
 * `fill` では塗り分けられない。recharts の `Cell` は deprecated なので、
 * `DevelopPointsTrendCard` と同じ shape レンダラー方式を使う。
 *
 * `dashed` を立てると「薄塗り + 破線枠」になり、処理中（まだ完了していない量）を表す。
 * @graph-connects none
 */
function createCategoryBarShape(opacity: number, dashed = false) {
  return function CategoryBar(props: unknown) {
    const p = props as {
      x?: number;
      y?: number;
      width?: number;
      height?: number;
      payload?: BarGroup;
    };
    const { x, y, width, height } = p;
    if (x == null || y == null || width == null || height == null) return <g />;
    const color = p.payload?.color ?? 'var(--color-text-muted)';
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={3}
        fill={color}
        fillOpacity={opacity}
        stroke={color}
        strokeWidth={dashed ? 1.5 : 1}
        strokeDasharray={dashed ? WIP_DASH_ARRAY : undefined}
      />
    );
  };
}

/** 予算バー（淡い塗り） @graph-connects none */
const budgetBarShape = createCategoryBarShape(BUDGET_BAR_OPACITY);
/** 実績（完了）バー（濃い塗り） @graph-connects none */
const actualBarShape = createCategoryBarShape(0.95);
/** 処理中バー（薄塗り + 破線枠） @graph-connects none */
const wipBarShape = createCategoryBarShape(WIP_FILL_OPACITY, true);

/** ツールチップ明細の 1 行 @graph-connects none */
interface DetailRow {
  label: string;
  value: string;
}

/**
 * 棒グラフのツールチップ明細。
 *
 * 積み棒を既定の series ごとの行で出すと、正規化後の比率が並ぶだけで読めない。
 * payload から生の pt を取り出し、**予算 / 完了 / 処理中（件数付き） / 合計 / 達成率**を
 * 1 カテゴリ分の明細として出す。欠測の行は出さない（0pt と欠測を区別する）。
 *
 * recharts の `content` へ渡すためモジュールトップレベルに置く（render 内で生成すると
 * 毎 render でコンポーネント型が変わり、ツールチップが再マウントされる）。
 * @graph-connects none
 */
function BarTooltipContent(props: unknown) {
  const { t } = useTranslation();
  const p = props as {
    active?: boolean;
    payload?: { payload?: BarGroup }[];
  };
  const group = p.payload?.[0]?.payload;
  if (!p.active || !group) return null;

  const rows: DetailRow[] = [];
  if (group.target !== null) {
    rows.push({
      label: t('kpi.requirementBreakdown.budget'),
      value: `${formatPt(group.target)}pt`,
    });
  }
  if (group.actual !== null) {
    rows.push({
      label: t('kpi.requirementBreakdown.completed'),
      value: `${formatPt(group.actual)}pt`,
    });
  }
  if (group.wip !== null) {
    const tickets =
      group.wipTickets === null
        ? ''
        : ` ${t('kpi.requirementBreakdown.tickets', { count: group.wipTickets })}`;
    rows.push({
      label: t('kpi.requirementBreakdown.inProgress'),
      value: `${formatPt(group.wip)}pt${tickets}`,
    });
  }
  if (group.actualTotal !== null) {
    rows.push({
      label: t('kpi.requirementBreakdown.total'),
      value: `${formatPt(group.actualTotal)}pt`,
    });
    if (group.target !== null && group.target > 0) {
      rows.push({
        label: t('kpi.requirementBreakdown.achievementRate'),
        value: `${String(Math.round((group.actualTotal / group.target) * 1000) / 10)}%`,
      });
    }
  }

  return (
    <div className="px-3 py-2 text-[11px] leading-[17px]" style={TOOLTIP_STYLE}>
      <p className="m-0 mb-1 font-semibold text-[var(--color-text-primary)]">{group.category}</p>
      <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
        {rows.map((row) => (
          <li key={row.label} className="flex items-baseline justify-between gap-3">
            <span className="text-[var(--color-text-secondary)]">{row.label}</span>
            <span className="tabular-nums text-[var(--color-text-primary)]">{row.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * 要件定義 / 開発pt の内訳ビュー。
 *
 * 左: 当月実績（完了）と現時点の処理中pt の構成比（円）。右: カテゴリ（要件定義 / 開発pt）
 * ごとの 予算 vs 実績（棒）で、実績は **完了（実塗り）の上に処理中（破線枠）を積む**。
 *
 * **要件定義は完了まで数週かかる工程が多く、完了実績だけ見ると「着手もしていない」ように
 * 読める**（完了 6pt に対し処理中 50pt のような月がある）。そのため完了と処理中を同じ
 * チャートに出し、色 = 工程 / 塗り = 状態（実塗り=完了・破線枠=処理中・淡い塗り=予算）という
 * 2 軸の読み方を円と棒で揃える。処理中pt は `actual_point` のみの口径で、週次pt集計表の
 * 「処理中pt」行と同一母集団（engineer team allowlist）。
 *
 * **予算は現時点進捗目標（JP 営業日で日割りした MTD 累積）**で、実績も当月累計。月間満額を
 * 置くと月初ほど実績が未達に見えて進捗を読めないため、折れ線ビューの目標線と同じ日割り基準に
 * 揃え、「今日時点で予算どおり進んでいるか」を同じ尺度で読めるようにする。
 *
 * 円の百分率は**スライスの外に出さず、下部の値リストで読ませる**。4 スライスのうち要件定義側は
 * 全体スコープで 1〜2% になり、指示線 + 外側ラベルでは文字がパネル幅に収まらず数字が切れる
 * （`88.9%` が `8.9%` に見える）。リストなら比率が小さいスライスでも pt と % を併記できる。
 * 円の中心には要件定義比率だけを残し、カード 1 枚の結論を 1 つにする。数字は要件定義の系統色
 * （水色）で出してキャプションは「処理中含む」だけにする。「要件定義比率」と書くと穴の幅に
 * 収まらずドーナツに重なるため、**何の比率かは色で示す**。
 *
 * 棒グラフは **x 軸を 要件定義 / 開発pt、系列を 予算 / 実績** に取り、**同じ色の 予算 と 実績 が
 * 隣り合う**ようにする（比べたいのは「その枠をどれだけ使ったか」で、要件定義と開発ptを並べて
 * 比べたいわけではないため）。この用途では両カテゴリを同一尺度に乗せる必要が無く、むしろ
 * 全体スコープでは要件定義側の棒が潰れて読めないので、**y 軸は出さずカテゴリごとに尺度を切り**、
 * 絶対値は各棒の数値ラベルで読ませる。ホバー時は 予算 / 完了 / 処理中（件数） / 合計 / 達成率 の
 * 明細を出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 開発pt (MTD) カードの「内訳」ビュー。要件定義 / 開発pt それぞれの 実績(完了) と 処理中pt を、色=工程・実塗り=完了・破線枠=処理中 で円グラフ（中心に要件定義比率を水色で表示、百分率は下部の値リスト）と積み棒グラフ（予算は淡い塗りで隣に並置、y 軸なし・カテゴリごとの尺度・数値ラベル付き）に描く。棒のホバーで 予算 / 完了 / 処理中(件数) / 合計 / 達成率 の明細を出す
 * @graph-connects PieChart [calls] 実績(完了) + 処理中 の構成比の円グラフ描画
 * @graph-connects BarChart [calls] 予算 vs 実績(完了 + 処理中) の積み棒グラフ描画
 * @graph-connects BarTooltipContent [calls] 棒グラフのツールチップ明細描画
 */
export function RequirementBreakdownCharts({
  requirementActual,
  developActual,
  requirementTarget,
  developTarget,
  wip,
}: RequirementBreakdownChartsProps) {
  const { t } = useTranslation();

  const requirementLabel = t('kpi.requirementDefinition');
  const developLabel = t('kpi.developPointExcludingRequirement');
  const completedLabel = t('kpi.requirementBreakdown.completed');
  const inProgressLabel = t('kpi.requirementBreakdown.inProgress');
  const sliceName = (category: string, state: string) =>
    t('kpi.requirementBreakdown.sliceName', { category, state });

  const pieData: PieSlice[] = [
    {
      name: sliceName(requirementLabel, completedLabel),
      value: requirementActual ?? 0,
      color: REQUIREMENT_COLOR,
      inProgress: false,
    },
    ...(wip
      ? [
          {
            name: sliceName(requirementLabel, inProgressLabel),
            value: wip.requirementPt,
            color: REQUIREMENT_COLOR,
            inProgress: true,
            tickets: wip.requirementTickets,
          },
        ]
      : []),
    {
      name: sliceName(developLabel, completedLabel),
      value: developActual ?? 0,
      color: DEVELOP_COLOR,
      inProgress: false,
    },
    ...(wip
      ? [
          {
            name: sliceName(developLabel, inProgressLabel),
            value: wip.excludingRequirementPt,
            color: DEVELOP_COLOR,
            inProgress: true,
            tickets: wip.excludingRequirementTickets,
          },
        ]
      : []),
  ];
  const pieTotal = pieData.reduce((sum, d) => sum + d.value, 0);
  // 円の中心に出す結論。要件定義（完了 + 処理中）が当月の投入量の何割か
  const requirementShare = slicePercent(
    (requirementActual ?? 0) + (wip?.requirementPt ?? 0),
    pieTotal,
  );

  const barData: BarGroup[] = [
    withCategoryScale({
      category: t('kpi.requirementBreakdown.axisRequirement'),
      color: REQUIREMENT_COLOR,
      target: requirementTarget,
      actual: requirementActual,
      wip: wip ? wip.requirementPt : null,
      wipTickets: wip ? wip.requirementTickets : null,
    }),
    withCategoryScale({
      category: t('kpi.requirementBreakdown.axisDevelop'),
      color: DEVELOP_COLOR,
      target: developTarget,
      actual: developActual,
      wip: wip ? wip.excludingRequirementPt : null,
      wipTickets: wip ? wip.excludingRequirementTickets : null,
    }),
  ];
  // 処理中しか無い（完了 0 / 予算欠測）カテゴリでも棒は描く。「着手済みだが未完了」は
  // 内訳ビューが一番出したい状態なので、ここで空状態に落としてはいけない
  const hasBarData = barData.some((d) => d.target !== null || d.actual !== null || d.wip !== null);

  const ptLabel = (value: number) => (value > 0 ? Math.round(value).toLocaleString() : '');

  return (
    <div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <p className="mb-1 text-xs text-[var(--color-text-secondary)]">
            {t('kpi.requirementBreakdown.ratioTitle')}
          </p>
          {pieTotal > 0 ? (
            <div className="relative h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    // 中心に要件定義比率を置くため、従来 (45%) より穴を広く取る。
                    // 外側はラベル / 指示線を外に出さなくなったぶん 78% → 92% まで広げる
                    // （スライスの弧が太くなり、1〜2% の処理中スライスも視認できる）
                    innerRadius="56%"
                    outerRadius="92%"
                    // 既定の開始位置は 3 時方向。読む順を揃えるため 12 時方向から時計回りに回す
                    startAngle={90}
                    endAngle={-270}
                    paddingAngle={2}
                    shape={sliceShape}
                  />
                  <Tooltip
                    wrapperStyle={{ zIndex: 50, outline: 'none' }}
                    contentStyle={TOOLTIP_STYLE}
                    labelStyle={{ color: 'var(--color-text-primary)' }}
                    formatter={
                      ((value: number, name: string, item: { payload?: PieSlice }) => {
                        const tickets = item.payload?.tickets;
                        const count =
                          tickets === undefined
                            ? ''
                            : ` ${t('kpi.requirementBreakdown.tickets', { count: tickets })}`;
                        return [
                          `${formatPt(value)}pt（${String(slicePercent(value, pieTotal))}%）${count}`,
                          name,
                        ];
                      }) as never
                    }
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* 中心の結論。tooltip を妨げないよう pointer-events を抜く。
                  数字は要件定義の系統色（水色）で出し、「何の比率か」を色で示す
                  （キャプションに「要件定義比率」と書くと穴に収まらずドーナツへ重なる）*/}
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span
                  className="text-lg font-bold leading-tight tabular-nums"
                  style={{ color: REQUIREMENT_COLOR }}
                >
                  {requirementShare}%
                </span>
                <span className="text-[10px] leading-tight text-[var(--color-text-muted)]">
                  {t('kpi.requirementBreakdown.shareLabel')}
                </span>
              </div>
            </div>
          ) : (
            <NoData label={t('status.noData')} />
          )}
        </div>

        <div>
          <p className="mb-1 text-xs text-[var(--color-text-secondary)]">
            {t('kpi.requirementBreakdown.budgetVsActualTitle')}
          </p>
          {hasBarData ? (
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ top: 16, right: 12, left: 4, bottom: 4 }}>
                  <XAxis
                    dataKey="category"
                    stroke="var(--color-text-muted)"
                    fontSize={11}
                    tickLine={false}
                    // カテゴリは 2 つだけ。間引かれて片方のラベルが消えないよう全ティックを描く
                    interval={0}
                  />
                  {/* 棒の高さはカテゴリ内で正規化した 0..1。共通尺度の目盛を出すと誤読させるので隠す */}
                  <YAxis hide domain={[0, BAR_RATIO_DOMAIN_MAX]} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 50, outline: 'none' }}
                    cursor={{ fill: 'var(--color-bg-elevated)', fillOpacity: 0.4 }}
                    content={BarTooltipContent as never}
                  />
                  <Bar
                    dataKey="targetRatio"
                    name="target"
                    // 実績側が積み棒（stackId あり）だと、stack を持たない系列は recharts の
                    // スロット計算で後ろへ回り 予算 が右に出る。予算にも単独の stackId を与えて
                    // 両方を stack 扱いにし、宣言順（予算 → 実績）＝左→右 を保つ
                    stackId="target"
                    maxBarSize={BAR_MAX_SIZE}
                    shape={budgetBarShape}
                  >
                    <LabelList
                      dataKey="target"
                      position="top"
                      fill="var(--color-text-secondary)"
                      fontSize={10}
                      formatter={ptLabel as never}
                    />
                  </Bar>
                  <Bar
                    dataKey="actualRatio"
                    name="actual"
                    stackId={wip ? 'actual' : undefined}
                    maxBarSize={BAR_MAX_SIZE}
                    shape={actualBarShape}
                  >
                    {/* 処理中を積むときは合計を積み棒の頂点に出す（下段の頂点に出すと上段と重なる） */}
                    {!wip && (
                      <LabelList
                        dataKey="actual"
                        position="top"
                        fill="var(--color-text-primary)"
                        fontSize={10}
                        formatter={ptLabel as never}
                      />
                    )}
                  </Bar>
                  {wip && (
                    <Bar
                      dataKey="wipRatio"
                      name="wip"
                      stackId="actual"
                      maxBarSize={BAR_MAX_SIZE}
                      shape={wipBarShape}
                    >
                      <LabelList
                        dataKey="actualTotal"
                        position="top"
                        fill="var(--color-text-primary)"
                        fontSize={10}
                        formatter={ptLabel as never}
                      />
                    </Bar>
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <NoData label={t('kpi.requirementBreakdown.noBudget')} />
          )}
        </div>
      </div>

      {/* 値リスト兼凡例。円の百分率はここで読ませる（スライス外のラベルは幅が足りず切れる） */}
      <ul className="m-0 mt-3 grid list-none grid-cols-1 gap-x-5 gap-y-1 p-0 text-[11px] leading-[17px] text-[var(--color-text-secondary)] sm:grid-cols-2">
        {pieData.map((entry) => (
          <li key={entry.name} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]"
              style={
                entry.inProgress
                  ? {
                      backgroundColor: tint(entry.color),
                      border: `1px dashed ${entry.color}`,
                    }
                  : { backgroundColor: entry.color }
              }
            />
            <span className="min-w-0 flex-1 truncate">{entry.name}</span>
            <span className="tabular-nums text-[var(--color-text-primary)]">
              {formatPt(entry.value)}pt
            </span>
            <span className="w-11 shrink-0 text-right tabular-nums text-[var(--color-text-muted)]">
              {slicePercent(entry.value, pieTotal)}%
            </span>
          </li>
        ))}
      </ul>
      {/* 棒グラフの 予算（淡い塗り）は円に無いので、塗りの意味だけ 1 行で補う */}
      <p className="m-0 mt-1.5 text-center text-[10px] leading-[14px] text-[var(--color-text-muted)]">
        {t('kpi.requirementBreakdown.encodingHint')}
      </p>
    </div>
  );
}

/** 空状態のプレースホルダ（円 / 棒で共用） @graph-connects none */
function NoData({ label }: { label: string }) {
  return (
    <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-[var(--color-border)]">
      <p className="px-3 text-center text-xs text-[var(--color-text-muted)]">{label}</p>
    </div>
  );
}
